import { db } from '@/lib/firebase';
import { addNotification } from '@/services/notificationService';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';

const MENTION_PATTERN = /(^|\s)@([a-zA-Z0-9._-]{1,30})/g;
const MENTION_BOUNDARY_PATTERN = /(^|[\s.,!?;:)'"\]}])/;

interface MentionableUser {
  id: string;
  username?: string;
  displayName: string;
}

interface NotifyCommentMentionsInput {
  actorUserId: string;
  actorName: string;
  actorPhoto?: string;
  commentText: string;
  postId?: string;
  commentId?: string;
  actionUrl?: string;
}

function normalizeHandle(value: string): string {
  return value.trim().toLowerCase();
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function isBoundaryCharacter(value?: string): boolean {
  if (!value) {
    return true;
  }

  return MENTION_BOUNDARY_PATTERN.test(value);
}

function findMentionedFriendIds(commentText: string, friends: MentionableUser[]): Set<string> {
  if (!commentText || friends.length === 0) {
    return new Set<string>();
  }

  const aliases = friends
    .flatMap((friend) => {
      const values = new Set<string>();

      if (friend.username) {
        values.add(friend.username.trim());
      }

      if (friend.displayName) {
        values.add(friend.displayName.trim());
      }

      return Array.from(values)
        .filter((value) => value.length > 0)
        .map((value) => ({ friendId: friend.id, alias: value }));
    })
    .sort((a, b) => b.alias.length - a.alias.length);

  const mentionedFriendIds = new Set<string>();
  const mentionStartPattern = /(^|\s)@/g;
  let mentionStartMatch: RegExpExecArray | null;

  while ((mentionStartMatch = mentionStartPattern.exec(commentText)) !== null) {
    const mentionStartIndex = mentionStartMatch.index + mentionStartMatch[0].length;
    const remaining = commentText.slice(mentionStartIndex);

    for (const alias of aliases) {
      const aliasPattern = new RegExp(`^${escapeRegExp(alias.alias)}`, 'i');
      const aliasMatch = aliasPattern.exec(remaining);
      if (!aliasMatch) {
        continue;
      }

      const boundaryChar = remaining.charAt(aliasMatch[0].length);
      if (!isBoundaryCharacter(boundaryChar)) {
        continue;
      }

      mentionedFriendIds.add(alias.friendId);
      break;
    }
  }

  return mentionedFriendIds;
}

export function extractMentionHandles(commentText: string): string[] {
  if (!commentText || typeof commentText !== 'string') {
    return [];
  }

  const found = new Set<string>();
  MENTION_PATTERN.lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = MENTION_PATTERN.exec(commentText)) !== null) {
    const handle = normalizeHandle(match[2] || '');
    if (handle) {
      found.add(handle);
    }
  }

  return Array.from(found);
}

async function getFriendIds(userId: string): Promise<string[]> {
  const friendshipsQuery = query(collection(db, 'friendships'), where('participants', 'array-contains', userId));

  const friendshipsSnapshot = await getDocs(friendshipsQuery);
  const friendIds = new Set<string>();

  friendshipsSnapshot.docs.forEach((snapshotDoc) => {
    const data = snapshotDoc.data() as { participants?: string[]; status?: string };
    if (data.status !== 'accepted') {
      return;
    }

    const participants = Array.isArray(data.participants) ? data.participants : [];
    const otherUserId = participants.find((participantId) => participantId !== userId);
    if (otherUserId) {
      friendIds.add(otherUserId);
    }
  });

  return Array.from(friendIds);
}

async function resolveMentionableFriends(userId: string): Promise<MentionableUser[]> {
  const friendIds = await getFriendIds(userId);
  if (friendIds.length === 0) {
    return [];
  }

  const friendDocs = await Promise.all(friendIds.map((friendId) => getDoc(doc(db, 'users', friendId))));

  return friendDocs
    .map((friendDoc): MentionableUser | null => {
      if (!friendDoc.exists()) {
        return null;
      }

      const data = friendDoc.data() as Record<string, unknown>;
      const username = typeof data.username === 'string' ? data.username.trim() : '';
      const displayName =
        (typeof data.name === 'string' && data.name.trim()) ||
        (typeof data.displayName === 'string' && data.displayName.trim()) ||
        username;

      if (!username && !displayName) {
        return null;
      }

      return {
        id: friendDoc.id,
        username: username || undefined,
        displayName,
      };
    })
    .filter((value): value is MentionableUser => Boolean(value));
}

export async function notifyCommentMentions(input: NotifyCommentMentionsInput): Promise<void> {
  if (!input.commentText || !input.commentText.includes('@')) {
    return;
  }

  const mentionableFriends = await resolveMentionableFriends(input.actorUserId);
  const mentionedFriendIds = findMentionedFriendIds(input.commentText, mentionableFriends);
  const targets = mentionableFriends.filter((friend) => mentionedFriendIds.has(friend.id));
  if (targets.length === 0) {
    return;
  }

  await Promise.allSettled(
    targets
      .filter((target) => target.id !== input.actorUserId)
      .map((target) =>
        addNotification(target.id, 'post_mention', {
          postId: input.postId,
          commentId: input.commentId,
          authorName: input.actorName,
          authorPhoto: input.actorPhoto,
          commentText: input.commentText,
          actionUrl: input.actionUrl,
        })
      )
  );
}
